import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Plus, Clock, Eye, ShieldCheck, Heart, Star, Globe, Users } from 'lucide-react';


import { useAuth } from '../context/AuthContext';
import API from '../api/axios';

import { formatTimeAgo } from '../utils/formatDate';
import { formatPrice } from '../utils/priceFormatter';

import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const PropertyCard = React.memo(({ property }) => {
    const { user } = useAuth();
    const location = useLocation();
    const [isWishlisted, setIsWishlisted] = React.useState(property.is_wishlisted || false);
    const [loadingWishlist, setLoadingWishlist] = React.useState(false);

    const mainImage = useMemo(() => property.images ? property.images.split(',')[0] : 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&q=80&w=800', [property.images]);

    const toggleWishlist = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) {
            alert('Please login to add to wishlist');
            return;
        }
        setLoadingWishlist(true);
        try {
            const res = await API.post('/wishlist/toggle', { propertyId: property.id });
            setIsWishlisted(res.data.isWishlisted);
        } catch (err) {
            console.error('Error toggling wishlist:', err);
        } finally {
            setLoadingWishlist(false);
        }
    };

    const displayLanguages = useMemo(() => {
        if (!property.communication_languages) return null;
        try {
            const langs = typeof property.communication_languages === 'string'
                ? JSON.parse(property.communication_languages)
                : property.communication_languages;
            return (Array.isArray(langs) && langs.length > 0) ? langs.join(', ') : null;
        } catch (e) {
            return null;
        }
    }, [property.communication_languages]);


    return (
        <motion.div
            id={`property-${property.id}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={` group bg-white rounded-3xl overflow-hidden transition-all duration-500 border border-slate-100 h-full1 flex flex-col group-hover:bg-gray-50 relative ${property.is_vip === 1 ? 'ring-2 ring-amber-400 ring-offset-4 shadow-[0_20px_50px_rgba(251,191,36,0.15)] hover:shadow-[0_30px_60px_rgba(251,191,36,0.25)]' : property.is_new ? 'shadow-[0_0_40px_rgba(59,130,246,0.15)] border-primary/20' : 'shadow-2xl hover:shadow-slate-950'}`}
        >
            {/* Wishlist Button - Placed first and with high z-index to ensure it stays interactive */}
            <button
                type="button"
                onClick={toggleWishlist}
                disabled={loadingWishlist}
                aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                className={`absolute top-4 right-4 w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-xl z-40 swiper-no-swiping ${isWishlisted ? 'bg-red-500 text-white scale-110' : 'bg-white/80 backdrop-blur-md text-slate-400 hover:text-red-500 hover:scale-105'} active:scale-90`}
            >
                <Heart size={20} fill={isWishlisted ? "currentColor" : "none"} />
            </button>

            <Link
                to={`/property/${property.id}`}
                state={{ background: location }}
                className="flex flex-col h-full"
            >
                <div className="relative h-56 w-full overflow-hidden shrink-0">
                    <motion.img
                        layoutId={`image-${property.id}`}
                        src={property.images ? mainImage.startsWith('http') ? mainImage : `/${mainImage.replace(/^\//, '')}` : mainImage}
                        alt={property.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transform-gpu will-change-transform transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                    />
                    <div className="absolute top-3 sm:top-4 left-3 sm:left-4 flex flex-wrap gap-1.5 sm:gap-2 pr-12">
                        {property.is_vip === 1 && (
                            <div className="bg-gradient-to-r from-amber-300 via-amber-500 to-amber-600 px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-full text-[8px] sm:text-[9px] font-black text-white shadow-lg uppercase tracking-wider flex items-center gap-1 border border-amber-200/50">
                                <Star size={10} fill="currentColor" strokeWidth={0} />
                                VIP
                            </div>
                        )}
                        <div className="bg-white/95 backdrop-blur-sm border border-slate-200 px-2.5 py-1 rounded-full text-[8px] sm:text-[10px] font-black text-slate-800 shadow-sm uppercase tracking-wider">
                            {property.classification_name || property.type}
                        </div>
                        <div className={`px-2.5 py-1 border border-transparent rounded-full text-[8px] sm:text-[10px] font-black text-white shadow-sm uppercase tracking-wider ${property.listing_type === 'Rent' ? 'bg-blue-600/90' : 'bg-red-500/90'}`}>
                            {property.listing_type}
                        </div>
                        {property.seller_is_trusted === 1 && (
                            <div className="bg-green-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-[8px] sm:text-[10px] font-black text-white shadow-sm uppercase tracking-wider flex items-center gap-1">
                                <ShieldCheck size={10} strokeWidth={3} />
                                <span className="hidden xs:inline">Trusted</span>
                            </div>
                        )}
                        {property.is_new && (
                            <div className="bg-primary/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-[8px] sm:text-[10px] font-black text-white shadow-lg uppercase tracking-wider animate-pulse">
                                New
                            </div>
                        )}
                    </div>

                </div>

                <div className="p-4 sm:p-6 flex flex-col flex-1 ">
                    <div className="flex justify-between items-start mb-2">
                        <motion.h3
                            layoutId={`title-${property.id}`}
                            className="text-xl font-bold text-slate-800 line-clamp-3 group-hover:text-primary transition-colors"
                        >
                            {property.title}
                        </motion.h3>
                    </div>



                    <div className="flex flex-wrap items-center gap-2 text-slate-500 mb-4 text-sm font-medium">
                        <div className="flex items-center gap-1.5 flex-1 w-full">
                            <MapPin size={16} className="text-primary" />
                            <span className="line-clamp-1 capitalize">{property.location}{property.city ? `, ${property.city}` : ''}</span>
                        </div>



                        <div className="flex items-center gap-2 w-full justify-between mt-1">

                            {property.created_at && (
                                <div className="flex items-center gap-1.5 text-slate-400 text-[10px] bg-slate-50 px-2.5 py-1 rounded-full font-black uppercase tracking-widest">
                                    <Clock size={12} />
                                    <span>{formatTimeAgo(property.created_at)}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] bg-slate-50 px-2.5 py-1 rounded-full font-black uppercase tracking-widest ml-auto">
                                <Eye size={12} className="text-primary" />
                                <span>{property.views || 0} views</span>
                            </div>
                        </div>
                    </div>

                    <p className="text-slate-500 text-sm line-clamp-2 mb-2 font-medium flex-1">
                        {property.description}
                    </p>

                    {displayLanguages && (
                        <div className="flex items-center gap-1.5 text-slate-400 text-[9px] font-black uppercase tracking-[0.1em] mb-0 bg-slate-50/50 px-1 py-2 rounded-xl border border-slate-100/50 w-fit">
                            <Globe size={12} className="text-secondary" />
                            <span className="line-clamp-1">{displayLanguages}</span>
                        </div>
                    )}
                    {property.seller_name && (
                        <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center">
                                <Users size={10} className="text-slate-500" />
                            </div>
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{property.seller_name || 'Institutional Partner'}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-50 mt-auto">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-red-600 uppercase tracking-widest leading-none mb-1">{property.price_type}</span>
                            <motion.span
                                layoutId={`price-${property.id}`}
                                className="text-2xl font-black text-slate-500 tracking-tighter"
                            >
                                <span className='text-red-600 text-sm'>LKR</span>  {formatPrice(property.price)}
                            </motion.span>
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-900 group-hover:bg-slate-900 group-hover:text-white">
                            <Plus size={20} />
                        </div>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
});

export default PropertyCard;
